import { request } from "../../utils/request";
import { isPublicRoute } from "../routes";

/**
 * Extract memberToken from URL search parameters
 * @param {URLSearchParams} searchParams - URL search parameters
 * @returns {string|null} - memberToken value or null if not found
 */
export const extractMemberToken = (searchParams) => {
  if (!searchParams) {
    return null;
  }

  return searchParams.get("memberToken");
};

/**
 * Determine if memberToken should be processed for the given pathname
 * @param {string} pathname - Current pathname
 * @returns {boolean} - true if memberToken should be processed
 */
export const shouldProcessMemberToken = (pathname) => {
  // Don't process on public routes (login/register pages)
  if (isPublicRoute(pathname)) {
    return false;
  }

  // Don't process on standalone routes (compiled-reports)
  if (pathname.startsWith("/compiled-reports/")) {
    return false;
  }

  return true;
};

/**
 * Validate memberToken by calling /auth/users/me endpoint
 * @param {string} memberToken - Token to validate
 * @returns {Promise<Object>} - Validation result with success, userData, or error
 */
export const validateMemberToken = async (memberToken) => {
  try {
    const userData = await request(
      `${process.env.REACT_APP_API_URL}/auth/users/me`,
      {
        method: "GET",
        headers: {
          Authorization: `Token ${memberToken}`,
        },
      }
    );

    return {
      success: true,
      userData,
    };
  } catch (error) {
    return {
      success: false,
      error,
    };
  }
};

/**
 * Create clean URL by removing memberToken parameter but preserving others
 * @param {string} pathname - Current pathname
 * @param {URLSearchParams} searchParams - Current search parameters
 * @returns {Object} - Object with pathname and search string
 */
export const cleanupUrl = (pathname, searchParams) => {
  const newSearchParams = new URLSearchParams(searchParams);
  newSearchParams.delete("memberToken");

  return {
    pathname,
    search: newSearchParams.toString(),
  };
};
